// src/pages/CheckoutPage.jsx
//
// No online payment on this storefront: the customer enters their
// delivery address and taps "Place Order". Then:
//   1. the order is saved via shopAPI.placeOrder()
//   2. the customer's order copy (PDF) downloads automatically
//   3. the order is sent to the admin on WhatsApp:
//        • Laptop/desktop — a tab is opened *immediately on the click*
//          (so the popup blocker allows it) and pointed at WhatsApp Web
//          with the order text once the order is saved.
//        • Mobile — browsers block popups that happen after waiting for
//          the server, so we open the WhatsApp app directly with the
//          whatsapp:// link. The confirmation screen also shows a big
//          "Send order on WhatsApp" link — a real tap always works.
//
// Note: a WhatsApp link can only carry text, never a file. The PDF is
// downloaded to the customer; "Share PDF" on the confirmation screen
// lets them send the file itself through the phone's share sheet.

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, CheckCircleIcon, PhoneIcon, ArrowDownTrayIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useCartStore, useAuthStore, useShopSettings } from '../store';
import { packingAmt, priceOf } from '../components/ui';
import { shopAPI } from '../services/api';
import { ADMIN_WHATSAPP_NUMBER, ADMIN_EMAIL, SHOP_NAME } from '../config/tenant';
import { buildInvoicePdf, pdfToFile } from '../utils/invoicePdf';
import { savePdf } from '../utils/savePdf';

/* Phones & tablets (incl. iPadOS, which reports itself as a Mac) */
const IS_MOBILE = typeof navigator !== 'undefined' && (
  /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile/i.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
);
import toast from 'react-hot-toast';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style:'currency', currency:'INR', maximumFractionDigits:0 }).format(n||0);

// Plain-text version of fmt() — WhatsApp/email don't render the Intl
// currency formatter's non-breaking space cleanly on every client.
const money = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
// charges can have paise (3% of 1,005 = 30.15)
const money2 = (n) => {
  const v = Number(n || 0), whole = Math.abs(v - Math.round(v)) < 0.005;
  return `Rs. ${v.toLocaleString('en-IN', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 })}`;
};
const fmt2 = (n) => money2(n).replace('Rs. ', '₹');

export default function CheckoutPage() {
  const navigate   = useNavigate();
  const { items, getSubtotal, getTotal, coupon, couponDiscount, clearCart } = useCartStore();
  const { customer } = useAuthStore();
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(null);
  const placedRef = useRef(false);   // set the moment an order succeeds (before the cart is cleared)
  const [sharedPdf, setSharedPdf] = useState(false);  // whether native share already handled it
  const [snapshot, setSnapshot] = useState(null);      // order data frozen at submit time, for the confirmation-screen buttons (cart is cleared by then)

  const [form, setForm] = useState({
    name:    customer?.name    || '',
    phone:   customer?.phone   || '',
    address: customer?.address || '',
    city:    customer?.city    || '',
    state:   '',
    pincode: '',
    notes:   '',
    password: '',
    confirm:  '',
  });

  const subtotal = getSubtotal();
  const total    = getTotal();

  // Packing / delivery charge — tenants.packing_charge_pct (0 = off)
  const { packingPct, packingLabel, load: loadSettings } = useShopSettings();
  useEffect(() => { loadSettings(); }, [loadSettings]);
  const packing    = packingAmt(total, packingPct);
  const grandTotal = Math.round(total + packing);
  const packingIsDelivery = /deliver/i.test(packingLabel);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (items.length === 0 && !success && !placedRef.current) {
    navigate('/products'); return null;
  }

  // Plain-text summary — used as the wa.me fallback message and as the
  // mailto: body. Kept separate from the PDF so both channels always
  // have *something* readable even where the richer PDF path can't run.
  const buildOrderText = (snap) => {
    const lines = [
      `New Order — ${SHOP_NAME}`,
      `Order ID: ${snap.orderId || 'Pending confirmation'}`,
      '',
      `Customer: ${snap.customer.name}`,
      `Phone: ${snap.customer.phone}`,
      `Address: ${snap.customer.address}`,
    ];
    if (snap.customer.notes) lines.push(`Notes: ${snap.customer.notes}`);
    lines.push('', `Items (${snap.items.length}):`);
    snap.items.forEach((it, i) => lines.push(`${i + 1}. ${it.name} x${it.qty} — ${money(it.unitPrice * it.qty)}`));
    lines.push('', `Subtotal: ${money(snap.subtotal)}`);
    if (snap.coupon?.amount > 0) lines.push(`Coupon (${snap.coupon.label}): -${money(snap.coupon.amount)}`);
    if (snap.packing?.amount > 0) lines.push(`${snap.packing.label} (${snap.packing.pct}%): +${money2(snap.packing.amount)}`);
    lines.push(`TOTAL: ${money(snap.total)}`);
    if (!(snap.packing?.amount > 0 && /deliver/i.test(snap.packing.label))) {
      lines.push('', 'Delivery charge not included — please confirm with the customer based on their location & parcel, and arrange delivery.');
    }
    return lines.join('\n');
  };

  const buildPdf = (snap) => buildInvoicePdf({
    orderId:  snap.orderId,
    customer: snap.customer,
    items:    snap.items,
    subtotal: snap.subtotal,
    coupon:   snap.coupon,
    packing:  snap.packing,
    total:    snap.total,
  });

  const downloadPdf = (snap) => {
    savePdf(buildPdf(snap), `Order-${snap.orderId || 'draft'}.pdf`);
  };

  const emailAdmin = (snap) => {
    if (!ADMIN_EMAIL) return; // no shop email configured yet
    const subject = `New order ${snap.orderId || ''} — ${snap.customer.name}`;
    const body = buildOrderText(snap) + '\n\n(Invoice PDF: please attach the downloaded copy — email links can\'t carry attachments.)';
    window.open(`mailto:${ADMIN_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const waWebUrl = (snap) =>
    `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(buildOrderText(snap))}`;

  const openWhatsAppText = (snap) => {
    const url = waWebUrl(snap);
    window.open(url, '_blank', 'noopener,noreferrer');
    return url;
  };

  /* Mobile: open the WhatsApp app straight away (no popup involved). */
  const openWhatsAppApp = (snap) => {
    const app = `whatsapp://send?phone=${ADMIN_WHATSAPP_NUMBER}&text=${encodeURIComponent(buildOrderText(snap))}`;
    try { window.location.href = app; } catch { /* app not installed — the button on screen still works */ }
  };

  // Tries to hand the PDF straight to WhatsApp via the native share
  // sheet; only wa.me (text-only) if the browser can't share files.
  const shareToWhatsApp = async (snap) => {
    try {
      const file = pdfToFile(buildPdf(snap), `Order-${snap.orderId || 'draft'}.pdf`);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Order ${snap.orderId || ''}`,
          text: buildOrderText(snap),
        });
        setSharedPdf(true);
        return true;
      }
    } catch (err) {
      if (err?.name === 'AbortError') { setSharedPdf(true); return true; } // user just closed the share sheet — don't also fire wa.me
    }
    openWhatsAppText(snap);
    return false;
  };

  const handlePlaceOrder = async () => {
    if (!form.name || !form.phone || !form.address || !form.city || !form.pincode) {
      toast.error('Fill all required fields'); return;
    }
    // Laptop: open the WhatsApp tab NOW, while this click still counts as
    // a user action — after the server call the popup blocker would stop it.
    let waWin = null;
    if (!IS_MOBILE) {
      waWin = window.open('', '_blank');
      if (waWin) {
        try {
          waWin.document.write('<title>Sending order…</title><p style="font:600 18px system-ui,sans-serif;padding:40px;text-align:center;color:#444">Sending your order to WhatsApp… 🎆</p>');
        } catch { /* ignore */ }
      }
    }
    setLoading(true);
    try {
      const r = await shopAPI.placeOrder({
        items: items.map(i => ({ product_id: i.id, qty: i.qty })),
        payments: [],
        discount_code: coupon?.name || undefined,
        shipping_address: `${form.address}, ${form.city}, ${form.state} ${form.pincode}`,
        notes: form.notes,
        // Packing charge the customer saw (server should recompute from
        // tenants.packing_charge_pct and store it on the sale)
        packing_charge_pct: packingPct,
        packing_charge_amt: packing,
        // No login on this storefront — every order is placed as a guest,
        // so the backend always gets a name/phone to attach it to.
        guest_name: form.name,
        guest_phone: form.phone,
      });
      const data = r.data.data;

      // Freeze everything the receipt needs — the cart is about to be
      // cleared, so `items`/`coupon` won't be readable afterwards.
      const snap = {
        orderId: data?.invoiceNo,
        customer: {
          name: form.name, phone: form.phone,
          address: `${form.address}, ${form.city}${form.state ? ', ' + form.state : ''} ${form.pincode}`.trim(),
          notes: form.notes,
        },
        items: items.map(it => {
          const listPrice = parseFloat(it.selling_price || 0);
          const unitPrice = (it.has_offer && it.offer_price != null) ? parseFloat(it.offer_price) : listPrice;
          return {
            name: it.name, qty: it.qty,
            unit: it.unit ? String(it.unit).toUpperCase() : '',
            listPrice,                    // sales price (before discount)
            unitPrice,                    // final price per unit (after discount)
          };
        }),
        subtotal,
        coupon: couponDiscount > 0 ? { label: coupon?.name, amount: couponDiscount } : null,
        packing: null,
        total: grandTotal,
      };
      // Prefer the server's figures when it reports the packing charge it
      // stored on the sale; otherwise use what the customer was shown.
      const srvAmt = data?.packingChargeAmt ?? data?.packing_charge_amt;
      const srvPct = data?.packingChargePct ?? data?.packing_charge_pct;
      if (srvAmt != null) {
        const amt = parseFloat(srvAmt) || 0;
        snap.packing = amt > 0 ? { label: packingLabel, pct: parseFloat(srvPct ?? packingPct) || 0, amount: amt } : null;
        if (data?.grandTotal != null) snap.total = parseFloat(data.grandTotal);
      } else if (packing > 0) {
        snap.packing = { label: packingLabel, pct: packingPct, amount: packing };
      }
      placedRef.current = true;
      setSnapshot(snap);
      setSuccess(data || { invoiceNo: snap.orderId });
      clearCart();
      window.scrollTo({ top: 0, behavior: 'smooth' });   // show the confirmation from the top

      // 1) Customer's copy of the order — downloads automatically
      try { downloadPdf(snap); } catch (err) { console.error('PDF download failed', err); }

      // 2) Send the order to the admin on WhatsApp
      if (waWin && !waWin.closed) {
        try { waWin.opener = null; } catch { /* ignore */ }
        waWin.location.href = waWebUrl(snap);          // laptop → WhatsApp Web tab
        toast.success('Order placed! WhatsApp opened in a new tab.', { duration: 3000 });
      } else if (IS_MOBILE) {
        // give the PDF download a moment to start, then open the app
        setTimeout(() => openWhatsAppApp(snap), 1500);
        toast.success('Order placed! Opening WhatsApp…', { duration: 3000 });
      } else {
        toast.success('Order placed! Tap "Send order on WhatsApp" below.', { duration: 4000 });
      }
    } catch (e) {
      if (waWin && !waWin.closed) waWin.close();
      toast.error(e.response?.data?.message || 'Order failed. Please try again.');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="pt-24 min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <motion.div initial={{ scale:0.8, opacity:0 }} animate={{ scale:1, opacity:1 }} transition={{ type:'spring' }}
          className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <motion.div initial={{ scale:0 }} animate={{ scale:1 }} transition={{ delay:0.2, type:'spring' }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="h-10 w-10 text-green-500"/>
          </motion.div>
          <h2 className="font-display font-black text-3xl text-gray-900 mb-2">We got your order! 🎆</h2>
          <p className="text-gray-500 mb-6">
            We've received your order details. Our team will call you shortly on{' '}
            <span className="font-semibold text-gray-700">{form.phone}</span> to confirm and arrange delivery.
          </p>
          <div className="bg-gray-50 rounded-2xl p-4 mb-6 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Order ID</span>
              <span className="font-bold font-mono">{success.invoiceNo}</span>
            </div>
            {snapshot?.packing?.amount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{snapshot.packing.label} ({snapshot.packing.pct}%)</span>
                <span className="font-semibold">{fmt2(snapshot.packing.amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">To Pay</span>
              <span className="font-bold text-red-600">{fmt(snapshot?.total ?? success.grandTotal ?? grandTotal)}</span>
            </div>
            {success.loyaltyEarned > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Loyalty Points Earned</span>
                <span className="font-bold text-green-600">+{success.loyaltyEarned} pts ⭐</span>
              </div>
            )}
          </div>

          <div className="space-y-2.5 mb-5">
            {snapshot && (
              <a
                href={waWebUrl(snapshot)}
                target="_blank" rel="noopener noreferrer"
                className="wa-send-btn w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-base font-extrabold text-white"
                style={{ background: '#25D366', boxShadow: '0 10px 24px rgba(37,211,102,.35)' }}
              >
                <PhoneIcon className="h-5 w-5" /> Send order on WhatsApp
              </a>
            )}
            <button
              onClick={() => snapshot && downloadPdf(snapshot)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white"
              style={{ background: 'linear-gradient(135deg,#d90429,#f77f00)' }}
            >
              <ArrowDownTrayIcon className="h-4 w-4" /> Download order copy (PDF)
            </button>
            <button
              onClick={() => snapshot && emailAdmin(snapshot)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border border-gray-200 text-gray-700"
            >
              <EnvelopeIcon className="h-4 w-4" /> Email us the order
            </button>
            <button
              onClick={() => snapshot && shareToWhatsApp(snapshot)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold text-gray-500"
            >
              <PhoneIcon className="h-3.5 w-3.5" /> {sharedPdf ? 'Share PDF again' : 'Share the PDF on WhatsApp'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-5 bg-green-50 border border-green-100 rounded-xl p-3">
            ✅ Your order copy (PDF) has been saved to your <b>Downloads</b>.
            If you can't find it, tap <b>Download order copy (PDF)</b> above.<br />
            If WhatsApp didn't open, tap <b>Send order on WhatsApp</b> so we receive your order.
          </p>

          <div className="flex gap-3">
            <button onClick={() => navigate('/products')} className="flex-1 btn-brand py-3 text-sm">Continue Shopping</button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="font-display font-black text-3xl text-gray-900 mb-2">Checkout</h1>
        <p className="text-sm text-gray-500 mb-8">
          No online payment needed — place your order and our team will call to confirm.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">

            <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} className="bg-white rounded-2xl p-6 border border-gray-100">
              <h2 className="font-bold text-gray-800 text-lg mb-5">Delivery Address</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className="shop-label">Full Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} className="shop-input" placeholder="Your full name" required/></div>
                <div className="col-span-2 sm:col-span-1"><label className="shop-label">Phone *</label><input value={form.phone} onChange={e => set('phone', e.target.value)} className="shop-input" placeholder="10-digit number" required/></div>
                <div className="col-span-2 sm:col-span-1"><label className="shop-label">Pincode *</label><input value={form.pincode} onChange={e => set('pincode', e.target.value)} className="shop-input" placeholder="6-digit pincode" required/></div>

                <div className="col-span-2"><label className="shop-label">Full Address *</label><textarea value={form.address} onChange={e => set('address', e.target.value)} className="shop-input h-20 resize-none" placeholder="House/Flat No., Street, Area..." required/></div>
                <div><label className="shop-label">City *</label><input value={form.city} onChange={e => set('city', e.target.value)} className="shop-input" placeholder="City" required/></div>
                <div><label className="shop-label">State</label><input value={form.state} onChange={e => set('state', e.target.value)} className="shop-input" placeholder="State"/></div>
                <div className="col-span-2"><label className="shop-label">Order Notes (optional)</label><input value={form.notes} onChange={e => set('notes', e.target.value)} className="shop-input" placeholder="Special instructions..."/></div>
              </div>
              <button onClick={handlePlaceOrder} disabled={loading}
                className="w-full btn-brand mt-5 justify-center py-3.5 text-base disabled:opacity-60">
                {loading ? 'Placing Order...' : `Place Order — ${fmt(grandTotal)}`}
              </button>
              <p className="text-xs text-gray-400 mt-3 text-center">
                We'll send your order PDF to our team and call you to confirm — no payment needed now.
              </p>
            </motion.div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-100 h-fit sticky top-24">
            <h3 className="font-bold text-gray-800 mb-4">Order Summary</h3>
            <div className="space-y-3 max-h-52 overflow-y-auto mb-4">
              {items.map(item => (
                <div key={item.id} className="flex gap-3 items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-50 to-orange-50 rounded-lg flex items-center justify-center text-xl flex-shrink-0 overflow-hidden">
                    {(item.thumb_url || item.image_url)
                      ? <img src={item.thumb_url || item.image_url} alt={item.name} className="w-full h-full object-cover"/>
                      : '🎆'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-700 truncate">{item.name}</p>
                    <p className="text-xs text-gray-400">Qty: {item.qty}</p>
                  </div>
                  <p className="text-sm font-bold text-red-600 flex-shrink-0">{fmt(priceOf(item).now * item.qty)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-100 pt-3 space-y-2">
              {[['Subtotal', fmt(subtotal)]].map(([l, v]) => (
                <div key={l} className="flex justify-between text-sm">
                  <span className="text-gray-500">{l}</span>
                  <span className="font-semibold">{v}</span>
                </div>
              ))}
              {couponDiscount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Coupon ({coupon?.name})</span>
                  <span className="font-bold">-{fmt(couponDiscount)}</span>
                </div>
              )}
              {packing > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{packingLabel} ({packingPct}%)</span>
                  <span className="font-semibold">+{fmt2(packing)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-100 pt-2 font-bold">
                <span className="text-gray-900">To Pay</span>
                <span className="text-red-600 text-xl font-black">{fmt(grandTotal)}</span>
              </div>
            </div>
            {!(packing > 0 && packingIsDelivery) && (
              <div className="mt-4 flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-xl p-3">
                <ShieldCheckIcon className="h-4 w-4 text-green-500 flex-shrink-0"/>
                Delivery charge depends on your location & parcel — our team will confirm it on call.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
